// frontend/src/pages/InventoryPage.tsx
import { useState, useMemo, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Package,
  Plus,
  ChefHat,
  Loader2,
  WifiOff,
  RefreshCw,
  ArrowLeftRight,
  UtensilsCrossed,
  Truck,
} from "lucide-react";
import { InventoryDenseRow } from "../components/InventoryDenseRow";
import { InventoryFilters } from "../components/InventoryFilters";
import { InventoryStats } from "../components/inventaire/InventoryStats";
import { AddItemDialog } from "../components/AddItemDialog";
import { ImageEditModal } from "../components/ImageEditModal";
import { ConfirmModal } from "../components/commandes/ConfirmModal";
import { SelectionActionBar } from "../components/SelectionActionBar";
import { ExportDialog } from "../components/ExportDialog";
import { InfoPanel } from "../components/InfoPanel";
import { useInventoryItems, useUpdateInventoryItem, useCreateInventoryItem, useAdjustQuantity } from "../hooks/useInventoryData";
import { useHistory } from "../contexts/HistoryContext";
import { Button } from "../components/ui/button";
import { TabGroup } from "../components/ui/TabGroup";

// Types centralisés - Directive #2
import type {
  InventoryItem,
  StockFilter,
  SortDirection,
  VisibleColumns,
  RecipeIngredient,
} from "../components/inventaire/inventaireTypes";

// Configuration components
import { OnboardingCallout } from "../components/onboarding/OnboardingCallout";
import { RecipeImportExport } from "../components/import-export/RecipeImportExport";
import { ResourceImportExport } from "../components/import-export/ResourceImportExport";
import { SupplierLinksPanel } from "../components/inventaire/SupplierLinksPanel";
import {
  ConfigurationStatsCards,
  ConfigurationProgressBar
} from "../components/configuration/ConfigurationStats";
import { RecipeToConfigureList } from "../components/configuration/RecipeToConfigureList";
import { ConfiguredRecipeList } from "../components/configuration/ConfiguredRecipeList";
import { RecipeConfigModal } from "../components/configuration/RecipeConfigModal";
import {
  getRecipeItems,
  getAvailableIngredients,
  isRecipeConfigured,
  calculateConfigurationStats,
  analyzeRecipe,
} from "../components/configuration/configurationUtils";

const CATEGORIES_ARRAY: string[] = [
  "Bières",
  "Cocktails",
  "Vins",
  "Boissons",
  "Collations",
  "Nourriture",
];

const LOW_STOCK_THRESHOLD_VALUE = 10;

export default function InventairePage() {
  const { addMovement } = useHistory();

  // Backend data integration
  const { data: inventoryData, isLoading, isError, error, refetch } = useInventoryItems();
  const updateItemMutation = useUpdateInventoryItem();
  const createItemMutation = useCreateInventoryItem();
  const adjustQuantityMutation = useAdjustQuantity();
  const isFromBackend = inventoryData?.fromBackend ?? false;

  // Tab state — initialisé depuis `?tab=stocks|recettes` (ex. « Aller aux recettes »
  // depuis la synchronisation des ventes navigue vers /inventaire?tab=recettes)
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");
  const [activeTab, setActiveTab] = useState<"stocks" | "recettes">(() =>
    tabParam === "recettes" ? "recettes" : "stocks"
  );
  useEffect(() => {
    if (tabParam === "stocks" || tabParam === "recettes") {
      setActiveTab(tabParam);
    }
  }, [tabParam]);

  // State - initialized from backend data, falls back to mock
  const [items, setItems] = useState<InventoryItem[]>([]);

  // Sync items from backend query
  useEffect(() => {
    if (inventoryData?.items) {
      setItems(inventoryData.items);
    }
  }, [inventoryData]);
  const [viewMode, setViewMode] = useState<"table" | "grid">("table");
  const [isMobile, setIsMobile] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("");
  const [stockFilter, setStockFilter] = useState<StockFilter>("tous");
  const [sortField, setSortField] = useState<keyof InventoryItem | "">("");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [selectionMode, setSelectionMode] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState<VisibleColumns>({
    image: true,
    nom: true,
    categorie: true,
    source: false,
    typeItem: false,
    quantite: true,
    prixVente: false, // Caché par défaut
    prixCoutant: false,
    marge: false,
    statut: true,
  });
  const [alertedLowStock, setAlertedLowStock] = useState(new Set<string>());
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isImageEditModalOpen, setIsImageEditModalOpen] = useState(false);
  const [imageEditData, setImageEditData] = useState<{
    id: string;
    image: string;
    nom: string;
  } | null>(null);
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);
  const [isMenuImportExportOpen, setIsMenuImportExportOpen] = useState(false);
  const [isResourceImportExportOpen, setIsResourceImportExportOpen] = useState(false);
  const [isSupplierLinksOpen, setIsSupplierLinksOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<{ type: 'single' | 'bulk'; id?: string; ids?: string[] } | null>(null);

  // Configuration state
  const [recipeConfigModalOpen, setRecipeConfigModalOpen] = useState(false);
  const [recipeToConfigure, setRecipeToConfigure] = useState<InventoryItem | null>(null);

  // Calculs pour configuration
  const recipeItems = useMemo(() => getRecipeItems(items), [items]);
  const availableIngredients = useMemo(() => getAvailableIngredients(items), [items]);
  const configStats = useMemo(() => calculateConfigurationStats(items), [items]);
  const recipesToConfigure = useMemo(
    () => recipeItems.filter((r) => !isRecipeConfigured(r)),
    [recipeItems]
  );
  const recipesConfigured = useMemo(
    () => recipeItems.filter((r) => isRecipeConfigured(r)),
    [recipeItems]
  );

  // Analysis map pour les recettes configurées
  const analysisMap = useMemo(() => {
    const map = new Map();
    recipesConfigured.forEach((recipe) => {
      map.set(recipe.id, analyzeRecipe(recipe, items));
    });
    return map;
  }, [recipesConfigured, items]);

  // Mobile detection and responsive view mode
  useEffect(() => {
    const checkMobile = () => {
      const isMobileScreen = window.innerWidth < 768; // md breakpoint
      setIsMobile(isMobileScreen);
      
      // Force grid mode on mobile
      if (isMobileScreen && viewMode === "table") {
        setViewMode("grid");
      }
    };

    // Check on mount
    checkMobile();

    // Check on resize
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, [viewMode]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyboard = (e: KeyboardEvent) => {
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement
      ) {
        return;
      }

      if (e.key === "Escape") {
        setIsAddDialogOpen(false);
        setIsImageEditModalOpen(false);
        setIsExportDialogOpen(false);
        setRecipeConfigModalOpen(false);
      } else if (e.ctrlKey && e.key === "a") {
        e.preventDefault();
        if (selectedIds.length === items.length) {
          setSelectedIds([]);
        } else {
          setSelectedIds(items.map((item) => item.id));
        }
      }
    };

    window.addEventListener("keydown", handleKeyboard);
    return () => window.removeEventListener("keydown", handleKeyboard);
  }, [items, selectedIds]);

  // Low stock alerts
  useEffect(() => {
    const lowStockItems = items.filter((item) => {
      const seuilStock = item.seuilStock ?? LOW_STOCK_THRESHOLD_VALUE;
      return item.quantite <= seuilStock && item.statut === "Actif";
    });

    lowStockItems.forEach((item) => {
      if (!alertedLowStock.has(item.id)) {
        setAlertedLowStock((prev) => new Set(prev).add(item.id));
      }
    });

    const newAlertedSet = new Set(alertedLowStock);
    alertedLowStock.forEach((itemId) => {
      const item = items.find((i) => i.id === itemId);
      if (item) {
        const seuilStock = item.seuilStock ?? LOW_STOCK_THRESHOLD_VALUE;
        if (item.quantite > seuilStock) {
          newAlertedSet.delete(itemId);
        }
      } else {
        newAlertedSet.delete(itemId);
      }
    });
    if (newAlertedSet.size !== alertedLowStock.size) {
      setAlertedLowStock(newAlertedSet);
    }
  }, [items, alertedLowStock]);

  // Filtering and sorting logic
  const filteredAndSortedItems = useMemo(() => {
    let filtered = items.filter((item) => {
      // Stocks tab: only show resources (not recipes/menu items)
      if (activeTab === "stocks" && item.typeItem === "recette") return false;

      const matchesSearch = item.nom
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
      const matchesCategory =
        !selectedCategory || item.categorie === selectedCategory;

      let matchesStock = true;
      if (stockFilter === "rupture") matchesStock = item.quantite === 0;
      else if (stockFilter === "bas") matchesStock = item.quantite <= 10;
      else if (stockFilter === "ok") matchesStock = item.quantite > 10;
      else if (stockFilter === "commander") matchesStock = item.inCart === true;

      return matchesSearch && matchesCategory && matchesStock;
    });

    if (sortField) {
      filtered = [...filtered].sort((a, b) => {
        let aValue: unknown = a[sortField];
        let bValue: unknown = b[sortField];

        if (typeof aValue === "string" && typeof bValue === "string") {
          aValue = aValue.toLowerCase();
          bValue = bValue.toLowerCase();
        }

        if ((aValue ?? 0) < (bValue ?? 0)) return sortDirection === "asc" ? -1 : 1;
        if ((aValue ?? 0) > (bValue ?? 0)) return sortDirection === "asc" ? 1 : -1;
        return 0;
      });
    }

    return filtered;
  }, [
    items,
    activeTab,
    searchTerm,
    selectedCategory,
    stockFilter,
    sortField,
    sortDirection,
  ]);

  // Handlers
  const handleSort = (field: keyof InventoryItem) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const handleQuantityChange = (id: string, newQuantity: number) => {
    const item = items.find((i) => i.id === id);
    if (item) {
      const oldQuantity = item.quantite;
      const diff = newQuantity - oldQuantity;

      // Optimistic local update
      setItems(items.map((i) => (i.id === id ? { ...i, quantite: newQuantity } : i)));

      // Persist to backend if connected
      if (isFromBackend) {
        adjustQuantityMutation.mutate(
          { id, quantity: diff, movementType: 'ADJUSTMENT', notes: `Ajustement UI: ${oldQuantity} → ${newQuantity}` },
          { onError: () => {
            // Revert on failure
            setItems(prev => prev.map((i) => (i.id === id ? { ...i, quantite: oldQuantity } : i)));
          }}
        );
      }

      addMovement({
        page: "Inventaire",
        itemId: id,
        itemNom: item.nom,
        type: "Ajustement quantité",
        details: `Quantité ajustée de ${oldQuantity} à ${newQuantity}`,
        ancienneValeur: `${oldQuantity}`,
        nouvelleValeur: `${newQuantity}`,
      });
    }
  };

  const handleFieldUpdate = (
    id: string,
    field: keyof InventoryItem,
    value: any
  ) => {
    const item = items.find((i) => i.id === id);
    if (item && item[field] !== value) {
      const oldValue = item[field];
      setItems(items.map((i) => (i.id === id ? { ...i, [field]: value } : i)));

      // Persist to backend if connected
      if (isFromBackend) {
        updateItemMutation.mutate(
          { id, changes: { [field]: value } as Partial<InventoryItem> },
          { onError: () => {
            // Revert on failure
            setItems(prev => prev.map((i) => (i.id === id ? { ...i, [field]: oldValue } : i)));
          }}
        );
      }

      const fieldNames: Record<string, string> = {
        nom: "Nom",
        categorie: "Catégorie",
        image: "Image",
        prixVente: "Prix de vente",
        prixCoutant: "Prix coûtant",
        statut: "Statut",
      };

      addMovement({
        page: "Inventaire",
        itemId: id,
        itemNom: item.nom,
        type: "Modification",
        details: `${fieldNames[field]} modifié`,
        ancienneValeur: String(oldValue),
        nouvelleValeur: String(value),
      });
    }
  };

  const handleDelete = (id: string) => {
    setDeleteConfirm({ type: 'single', id });
  };

  const confirmDelete = () => {
    if (!deleteConfirm) return;
    if (deleteConfirm.type === 'single' && deleteConfirm.id) {
      const item = items.find((i) => i.id === deleteConfirm.id);
      if (item) {
        setItems(items.filter((i) => i.id !== deleteConfirm.id));
        addMovement({
          page: "Inventaire",
          itemId: deleteConfirm.id,
          itemNom: item.nom,
          type: "Suppression",
          details: `Article supprimé de l'inventaire`,
        });
      }
    } else if (deleteConfirm.type === 'bulk' && deleteConfirm.ids) {
      setItems(items.filter((item) => !deleteConfirm.ids!.includes(item.id)));
      addMovement({
        page: "Inventaire",
        type: "Suppression en masse",
        details: `${deleteConfirm.ids.length} article(s) supprimé(s)`,
      });
      setSelectedIds([]);
    }
    setDeleteConfirm(null);
  };

  const handleAddItem = (newItem: Omit<InventoryItem, "id" | "source" | "typeItem">) => {
    if (isFromBackend) {
      // Create via backend API
      createItemMutation.mutate(newItem, {
        onSuccess: () => {
          refetch(); // Refresh the list from backend
          addMovement({
            page: "Inventaire",
            itemNom: newItem.nom,
            type: "Ajout",
            details: `Nouvel article ajouté - Catégorie: ${newItem.categorie}, Qté: ${newItem.quantite}`,
          });
        },
      });
    } else {
      // Fallback: local-only (mock mode)
      const maxId = items.reduce((max, item) => {
        const itemNum = parseInt(item.id);
        return itemNum > max ? itemNum : max;
      }, 0);

      const id = String(maxId + 1).padStart(4, "0");
      const fullItem: InventoryItem = {
        ...newItem,
        id,
        source: "manuel" as const,
        typeItem: newItem.prixVente > 0 ? "produit_simple" as const : "ingredient" as const,
      };
      setItems([...items, fullItem]);

      addMovement({
        page: "Inventaire",
        itemId: id,
        itemNom: newItem.nom,
        type: "Ajout",
        details: `Nouvel article ajouté - Catégorie: ${newItem.categorie}, Qté: ${newItem.quantite}`,
      });
    }
  };

  const handleBulkEdit = (
    itemIds: string[],
    updates: Partial<
      Pick<
        InventoryItem,
        "categorie" | "statut" | "prixVente" | "prixCoutant" | "seuilStock"
      >
    >
  ) => {
    const updatedItems = items.map((item) => {
      if (itemIds.includes(item.id)) {
        return { ...item, ...updates };
      }
      return item;
    });

    setItems(updatedItems);

    addMovement({
      page: "Inventaire",
      type: "Modification en masse",
      details: `${itemIds.length} article(s) modifié(s)`,
    });

    setSelectedIds([]);
  };

  const handleBulkDelete = (itemIds: string[]) => {
    setDeleteConfirm({ type: 'bulk', ids: itemIds });
  };

  const handleToggleCart = (id: string) => {
    const item = items.find((i) => i.id === id);
    if (item) {
      const newInCart = !item.inCart;
      setItems(items.map((i) => (i.id === id ? { ...i, inCart: newInCart } : i)));

      addMovement({
        page: "Inventaire",
        itemId: id,
        itemNom: item.nom,
        type: newInCart ? "Ajout au panier" : "Retrait du panier",
        details: newInCart
          ? "Article marqué pour commande"
          : "Article retiré du panier",
      });
    }
  };

  const handleImageEdit = (id: string, image: string, nom: string) => {
    setImageEditData({ id, image, nom });
    setIsImageEditModalOpen(true);
  };

  const handleImageSave = (id: string, newImage: string) => {
    handleFieldUpdate(id, "image", newImage);
    setIsImageEditModalOpen(false);
  };

  // Configuration handlers
  const handleConfigureRecipe = (recipe: InventoryItem) => {
    setRecipeToConfigure(recipe);
    setRecipeConfigModalOpen(true);
  };

  const handleSaveConfiguration = (recipeId: string, composition: RecipeIngredient[]) => {
    setItems(items.map((item) =>
      item.id === recipeId
        ? { ...item, composition, typeItem: "recette" as const }
        : item
    ));

    addMovement({
      page: "Inventaire - Configuration",
      itemId: recipeId,
      itemNom: items.find((i) => i.id === recipeId)?.nom || "",
      type: "Configuration recette",
      details: `Composition définie avec ${composition.length} ingrédients`,
    });

    setRecipeConfigModalOpen(false);
  };

  // Show loading state on initial load
  if (isLoading && items.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 gap-3 text-[var(--muted-foreground)]">
        <Loader2 className="w-5 h-5 animate-spin" />
        <span>Chargement de l'inventaire...</span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Guided onboarding shortcut — driven by real state, self-dismissing */}
      <OnboardingCallout />

      {/* Data source indicator */}
      {!isFromBackend && !isLoading && (
        <div className="flex items-center gap-2 px-3 py-2 text-sm rounded-lg bg-amber-500/10 text-amber-400 border border-amber-500/20">
          <WifiOff className="w-4 h-4 flex-shrink-0" />
          <span>Mode hors-ligne — données de démonstration. Connectez-vous au serveur pour voir vos vrais stocks.</span>
          <button onClick={() => refetch()} className="ml-auto flex items-center gap-1 hover:text-amber-300 transition-colors">
            <RefreshCw className="w-3.5 h-3.5" />
            Réessayer
          </button>
        </div>
      )}

      {/* Error banner */}
      {isError && (
        <div className="flex items-center gap-2 px-3 py-2 text-sm rounded-lg bg-red-500/10 text-red-400 border border-red-500/20">
          <span>Erreur: {(error as Error)?.message || 'Impossible de charger les données'}</span>
          <button onClick={() => refetch()} className="ml-auto hover:text-red-300 transition-colors">
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center gap-2">
        <h2 className="text-2xl md:text-3xl font-bold text-[var(--foreground)] text-shadow-subtle">
          Inventaire
        </h2>
        <div className="flex items-center">
          <InfoPanel
            title="À propos"
            description="Gérez votre inventaire avec des filtres avancés, tri dynamique, modification en masse et export Excel. Configurez le mapping entre vos recettes POS et leurs ingrédients pour automatiser le suivi des stocks."
            icon={Package}
          />
        </div>
      </div>

      {/* Tabs with Action Buttons */}
      <TabGroup
        value={activeTab}
        onChange={setActiveTab}
        tabs={[
          { value: "stocks", label: "Stocks", icon: Package },
          { value: "recettes", label: "Menu & Recettes", icon: UtensilsCrossed },
        ]}
        actions={
          <>
            {activeTab === "stocks" ? (
              <>
                <Button
                  variant="secondary"
                  size="sm"
                  icon={Truck}
                  hideTextOnMobile
                  onClick={() => setIsSupplierLinksOpen(true)}
                >
                  Liens fournisseurs
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  icon={ArrowLeftRight}
                  hideTextOnMobile
                  onClick={() => setIsResourceImportExportOpen(true)}
                >
                  Importer / Exporter
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  icon={Plus}
                  hideTextOnMobile
                  onClick={() => setIsAddDialogOpen(true)}
                >
                  Ajouter
                </Button>
              </>
            ) : activeTab === "recettes" ? (
              <Button
                variant="secondary"
                size="sm"
                icon={ArrowLeftRight}
                hideTextOnMobile
                onClick={() => setIsMenuImportExportOpen(true)}
              >
                Importer / Exporter
              </Button>
            ) : null}
          </>
        }
      />

      {/* Tab Content */}
      {activeTab === "stocks" && (
        <>
          {/* Stats */}
          <InventoryStats items={items.filter(i => i.typeItem !== "recette")} lowStockThreshold={LOW_STOCK_THRESHOLD_VALUE} />

          {/* Filters */}
          <InventoryFilters
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            selectedCategory={selectedCategory}
            onCategoryChange={setSelectedCategory}
            categories={CATEGORIES_ARRAY}
            stockFilter={stockFilter}
            onStockFilterChange={(v: string) => setStockFilter(v as StockFilter)}
            visibleColumns={visibleColumns as unknown as Record<string, boolean>}
            onVisibleColumnsChange={(cols) => setVisibleColumns(cols as unknown as VisibleColumns)}
            isMobile={isMobile}
            selectionMode={selectionMode}
            onToggleSelectionMode={() => {
              setSelectionMode(!selectionMode);
              if (selectionMode) {
                setSelectedIds([]);
              }
            }}
          />

          {/* Content - Dense View */}
          <div className="space-y-1.5">
            {filteredAndSortedItems.map((item) => (
              <InventoryDenseRow
                key={item.id}
                item={item}
                isSelected={selectedIds.includes(item.id)}
                onToggleSelection={(id) => {
                  if (!selectionMode) return; // Ne pas sélectionner si mode désactivé
                  if (selectedIds.includes(id)) {
                    setSelectedIds(selectedIds.filter((selectedId) => selectedId !== id));
                  } else {
                    setSelectedIds([...selectedIds, id]);
                  }
                }}
                onQuantityChange={handleQuantityChange}
                onFieldUpdate={handleFieldUpdate}
                onImageEdit={handleImageEdit}
                categories={CATEGORIES_ARRAY}
                lowStockThreshold={LOW_STOCK_THRESHOLD_VALUE}
                selectionMode={selectionMode}
              />
            ))}
          </div>

          {/* Selection Action Bar */}
          {selectedIds.length > 0 && (
            <SelectionActionBar
              selectedCount={selectedIds.length}
              onBulkEdit={(updates) => handleBulkEdit(selectedIds, updates)}
              onExport={() => setIsExportDialogOpen(true)}
              onClearSelection={() => setSelectedIds([])}
              categories={CATEGORIES_ARRAY}
            />
          )}
        </>
      )}

      {/* Menu Items & Recipes Tab */}
      {activeTab === "recettes" && (
        <div className="space-y-6">
          {/* Configuration Stats */}
          <ConfigurationStatsCards stats={configStats} />
          <ConfigurationProgressBar stats={configStats} />

          {/* Section "Recettes configurées" — affichée en premier pour mettre en avant
              le travail accompli et pas obliger à scroller. Aperçu par défaut, expandable. */}
          <div className="bg-[var(--card)] rounded-lg border border-[var(--border)] p-6">
            <ConfiguredRecipeList
              recipes={recipesConfigured}
              analysisMap={analysisMap}
              onEdit={handleConfigureRecipe}
            />
          </div>

          {/* Section "À configurer" — affichée en second, en aperçu également. */}
          {recipesToConfigure.length > 0 && (
            <div className="bg-[var(--card)] rounded-lg border border-[var(--border)] p-6">
              <RecipeToConfigureList
                recipes={recipesToConfigure}
                onConfigure={handleConfigureRecipe}
              />
            </div>
          )}
        </div>
      )}

      {/* Modals */}
      {isAddDialogOpen && (
        <AddItemDialog
          isOpen={isAddDialogOpen}
          onClose={() => setIsAddDialogOpen(false)}
          onAdd={handleAddItem}
          categories={CATEGORIES_ARRAY}
        />
      )}

      {isImageEditModalOpen && imageEditData && (
        <ImageEditModal
          isOpen={isImageEditModalOpen}
          onClose={() => setIsImageEditModalOpen(false)}
          onSave={(imageUrl: string) => handleImageSave(imageEditData.id, imageUrl)}
          currentImage={imageEditData.image}
          itemName={imageEditData.nom}
        />
      )}

      {isExportDialogOpen && (
        <ExportDialog
          isOpen={isExportDialogOpen}
          onClose={() => setIsExportDialogOpen(false)}
          items={filteredAndSortedItems}
          selectedIds={selectedIds}
        />
      )}

      {isMenuImportExportOpen && (
        <RecipeImportExport
          isOpen={isMenuImportExportOpen}
          onClose={() => setIsMenuImportExportOpen(false)}
        />
      )}

      {isResourceImportExportOpen && (
        <ResourceImportExport
          isOpen={isResourceImportExportOpen}
          onClose={() => setIsResourceImportExportOpen(false)}
        />
      )}

      {isSupplierLinksOpen && (
        <SupplierLinksPanel
          isOpen={isSupplierLinksOpen}
          onClose={() => setIsSupplierLinksOpen(false)}
        />
      )}

      {recipeConfigModalOpen && recipeToConfigure && (
        <RecipeConfigModal
          isOpen={recipeConfigModalOpen}
          onClose={() => setRecipeConfigModalOpen(false)}
          recipe={recipeToConfigure}
          availableIngredients={availableIngredients}
          allItems={items}
          onSave={handleSaveConfiguration}
        />
      )}

      {/* Confirm Delete Modal */}
      <ConfirmModal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        onConfirm={confirmDelete}
        variant="danger"
        title={deleteConfirm?.type === 'bulk'
          ? `Supprimer ${deleteConfirm.ids?.length} article(s)`
          : 'Supprimer cet article'}
        description={deleteConfirm?.type === 'bulk'
          ? `Êtes-vous sûr de vouloir supprimer ${deleteConfirm.ids?.length} article(s) ? Cette action est irréversible.`
          : 'Êtes-vous sûr de vouloir supprimer cet article ? Cette action est irréversible.'}
        confirmText="Supprimer"
        cancelText="Annuler"
      />
    </div>
  );
}